import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ActivityIndicator, useActivity, useUnfinishedRuns } from './activity/index.js';
import { api, setUnauthorizedHandler } from './api.js';
import { AtlasRail } from './AtlasRail.js';
import { chromeForPath } from './chrome.js';
import { JourneyStrip } from './JourneyStrip.js';
import { LoginScreen } from './LoginScreen.js';
import { resolveRoute, usePathname } from './router.js';
import { setStreamSession, sharedActivityTransport } from './stream.js';
import { registeredSurfaces } from './surfaces.js';

type Access = 'unknown' | 'open' | 'closed';

export function Studio(): ReactNode {
  const [access, setAccess] = useState<Access>('unknown');
  const pathname = usePathname();
  // One compact indicator, mounted once, visible from every screen. It only subscribes
  // once there is a session: the activity snapshot is an authenticated read, and one
  // made before the operator signs in comes back refused and never comes back at all.
  const activity = useActivity(sharedActivityTransport, access === 'open');
  // A Bulk Run that is paused or waiting on an allowance has no Job in flight, and is
  // still the operator's to finish. R177 asks for it here rather than only on its own
  // surface, so the shell is what knows about it.
  const runs = useUnfinishedRuns(access === 'open');

  // Any 401, from any call, closes the shell - the background ones included: the
  // activity snapshot is read through the same client, and the event stream, which
  // cannot read a status of its own, asks after every drop. Nothing rewrites the URL,
  // so the route the operator was on is still the route that renders once they sign
  // back in. What this cannot do is notice a session that ended while nothing was
  // asked: a 401 has to be heard, and an idle tab makes no calls to hear one on.
  useEffect(() => {
    setUnauthorizedHandler(() => setAccess('closed'));
    return () => setUnauthorizedHandler(null);
  }, []);

  const check = useCallback(async () => {
    try {
      await api.readSession();
      setAccess('open');
    } catch {
      setAccess('closed');
    }
  }, []);

  // Re-read on every route change: an expired session is discovered on the move the
  // operator makes, not only on a full page load.
  useEffect(() => {
    void check();
  }, [check, pathname]);

  // The stream only runs while there is a session to stream to. A tab left open after
  // signing out goes quiet instead of knocking on `/events` for the rest of the day.
  useEffect(() => {
    setStreamSession(access === 'open');
  }, [access]);

  const signIn = useCallback(async (password: string) => {
    await api.signIn(password);
    setAccess('open');
  }, []);

  const signOut = useCallback(() => {
    void api.signOut().finally(() => setAccess('closed'));
  }, []);

  if (access === 'unknown') return <main className="booting" aria-busy="true" />;
  if (access === 'closed') return <LoginScreen onSignIn={signIn} />;

  const matched = resolveRoute(registeredSurfaces, pathname);
  const body = matched === null ? <NoSuchSurface /> : matched.surface.render(matched.params);

  return chromeForPath(pathname) === 'journey' ? (
    <div className="shell shell-journey">
      <JourneyStrip onSignOut={signOut} />
      <ActivityIndicator activity={activity} runs={runs} />
      <div className="workspace">{body}</div>
    </div>
  ) : (
    <div className="shell shell-atlas">
      <AtlasRail onSignOut={signOut} />
      <ActivityIndicator activity={activity} runs={runs} />
      <div className="workspace">{body}</div>
    </div>
  );
}

function NoSuchSurface(): ReactNode {
  return (
    <section className="page">
      <p className="eyebrow">Здесь ничего нет</p>
      <h1>У этого маршрута ещё нет экрана.</h1>
    </section>
  );
}
